import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import './../Lease Module/CustomBottomNav.css';
import GuestLoginModal from '../../GuestLoginModal';
import { AuthContext } from '../../AuthContext/AuthContext';

// ── Base nav items (default = List tab) ────────────────────────────────────
const BASE_NAV_ITEMS = [
  { key: 'home',  icon: <HomeIcon />,               route: '/dashboard',   label: 'Home' },
  { key: 'list',  icon: <FormatListBulletedIcon />, route: '/jvjd-list',   label: 'List' },
  { key: 'saves', icon: <FavoriteBorderIcon />,     route: '/jvjd-saves',  label: 'Saves' },
  { key: 'inbox', icon: <MailOutlineIcon />,        route: '/jvjd-inbox',  label: 'Inbox' },
];

// ── Map item — used in place of List on details AND list page ─────────────
const MAP_ITEM = {
  key: 'map',
  icon: <MapIcon />,
  route: '/jvjd',   // ✅ navigate here when Map icon is tapped
  label: 'Map',
};

const CustomBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentKey, setCurrentKey] = useState('');
  const navRef = useRef(null);
  const { isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const guestRestrictedTabs = ['saves', 'inbox'];

  // ✅ Detect pages that should show MAP instead of LIST
  const isDetailsPage = location.pathname.startsWith('/jv-jv-property-detail');
  const isListPage    = location.pathname.startsWith('/jvjd-list');

  const navItems = (isDetailsPage || isListPage)
    ? BASE_NAV_ITEMS.map(item => (item.key === 'list' ? MAP_ITEM : item))
    : BASE_NAV_ITEMS;

  // ── Determine active key ────────────────────────────────────────────────
  useEffect(() => {
    // Special case: on /jvjd or /jvjd-map the active tab should be 'map'
    if (location.pathname.startsWith('/jvjd') || location.pathname.startsWith('/jvjd-map')) {
      setCurrentKey('map');
      return;
    }

    const activeItem =
      navItems.find(item => location.pathname.startsWith(item.route))?.key ||
      'home';
    setCurrentKey(activeItem);
  }, [location.pathname, navItems]);

  const handleNav = (item) => {
    if (isGuest && guestRestrictedTabs.includes(item.key)) {
      setShowGuestModal(true);
      return;
    }
    setCurrentKey(item.key);
    navigate(item.route);
  };

  // ── Move indicator ──────────────────────────────────────────────────────
  useEffect(() => {
    const current = navRef.current?.querySelector(
      `.nav-item[data-key="${currentKey}"]`
    );
    const indicator = navRef.current?.querySelector('.nav-indicator');
    if (current && indicator) {
      const { offsetLeft, offsetWidth } = current;
      indicator.style.left = `${offsetLeft + offsetWidth / 2 - 25}px`;
    }
  }, [currentKey, navItems]);

  return (
    <div className="nav-wrapper">
      <div className="nav-bar" ref={navRef}>
        {navItems.map((item) => (
          <div
            key={item.key}
            data-key={item.key}
            className={`nav-item ${currentKey === item.key ? 'nav-item-active' : ''}`}
            onClick={() => handleNav(item)}
          >
            {item.icon}
            <div className="nav-label">{item.label}</div>
          </div>
        ))}
        <div className="nav-indicator" />
      </div>

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />
    </div>
  );
};

export default CustomBottomNav;